/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { HeaderNavBarDropMenu } from '@/components/content/Header/parts/NavBarDropMenu';
import { headerNavBarContainerSX } from '@/components/content/Header/styles/navBar/container';
import { containerHeader } from '@/components/content/Header/styles/navBar/container';
import { containerHeaderCenter } from '@/components/content/Header/styles/navBar/container';
import { Linkable, LinkWrap } from '@/components/blocks/Linkable';
import { useNavigation } from '@/data/Navigation';
import { Paper, Stack, Grid, Box, Typography, IconButton, Fade } from '@mui/material';
import { useHeader } from '@/data/Content/Header';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { HeaderSearch, HeaderSearchHandle } from '@/components/content/Header/parts/Search';
import { HeaderFavorite } from '@/components/content/Header/parts/Favorite';
import { HeaderMiniCart } from '@/components/content/Header/parts/MiniCart';
import { HeaderAccount } from '@/components/content/Header/parts/Account';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { combineSX } from '@/utils/combineSX';
import { RenderProductImg } from '@/components/blocks/RenderProductImg';
import { Img } from '@/components/blocks/MaterialImage';
import SearchIcon from '@mui/icons-material/Search';
import { PriceProductSearch } from '@/components/content/Header/parts/PriceProductSearch';
import CloseIcon from '@mui/icons-material/Close';
import { useSearchNavigation } from '@/data/Content/SearchNavigation';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import { HeaderNavBarSuggest } from '@/components/content/Header/parts/HeaderNavBarSuggest';
import { HeaderCategoryTemporal } from '@/components/content/Header/parts/HeaderCategoryTemporal';
import { HeaderNavBarContentRecommendation } from '@/components/content/Header/parts/HeaderNavBarContentRecommendation';

type Option = {
	label: string;
	href?: string;
	identifier: string;
};
// eslint-disable-next-line complexity
export const HeaderNavBar = () => {
	const id = 'header';
	const { onSubmit } = useSearchNavigation();
	// eslint-disable-next-line func-call-spacing
	const headerSearchRef = useRef<HeaderSearchHandle>(null);
	const { contentItems } = useHeader(id);
	const { navigation } = useNavigation();
	const [isSearchFocused, setIsSearchFocused] = useState(false);
	const [isContainerVisible, setContainerVisible] = useState(false);
	const [isCloseIconVisible, setCloseIconVisible] = useState(false);
	const [suggest, setSuggest] = useState<any[]>([]);
	const [suggestProducts, setSuggestProducts] = useState<any[]>([]);
	const [searchValueExternal, setSearchValueExternal] = useState<string>('');

	const options = useMemo<Option[]>(
		() =>
			suggest
				.map(({ identifier, entry }) =>
					identifier && entry
						? entry.map(({ label, href }: { label: string; href?: string }) => ({
								label,
								href,
								identifier,
						  })) || []
						: []
				)
				.flat(1)
				.filter((elm) => elm.identifier === 'Keyword'),
		[suggest]
	);

	const handleSearchFocus = () => {
		setContainerVisible(true);
		setIsSearchFocused(true);
		setCloseIconVisible(true);
	};

	const handleSearchBlur = useCallback(() => {
		setContainerVisible(false);
		setIsSearchFocused(false);
		setCloseIconVisible(false);
	}, []);

	const handleSuggestChange = (newSuggest: any) => {
		setSuggest(newSuggest);
	};

	const handleSuggestProductsChange = (newSuggestProducts: any) => {
		setSuggestProducts(newSuggestProducts);
	};

	const handleSearchValue = (newSearchValue: any) => {
		setSearchValueExternal(newSearchValue);
	};

	const handleSearchSubmit = (value: { label: string }) => {
		onSubmit(value);
		handleSearchBlur();
		handleClearSearchFromNavBar();
	};

	const handleClearSearchFromNavBar = () => {
		if (headerSearchRef.current) {
			headerSearchRef.current.clearSearch();
		}
	};

	const handleOnClickEntryRecommendation = () => {
		setContainerVisible(false);
		setIsSearchFocused(false);
		setCloseIconVisible(false);
	};

	return (
		<Paper sx={headerNavBarContainerSX}>
			<Grid sx={containerHeader}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="flex-start"
						maxWidth="163px"
						width="100%"
						spacing={2}
						sx={combineSX([isSearchFocused ? { maxWidth: '150px' } : {}, containerHeaderCenter])}
					>
						{contentItems.map((properties) => (
							<ContentRecommendation
								key={properties.emsName}
								id={`${id}-${properties.emsName}`}
								properties={properties}
							/>
						))}
					</Stack>

					{!isSearchFocused ? (
						<Fade in={!isContainerVisible} timeout={1000}>
							<Stack
								direction="row"
								alignItems="stretch"
								spacing={{ xs: 1, lg: 2 }}
								sx={{ height: '66px' }}
							>
								{navigation?.map(({ label, url, children }) => (
									<LinkWrap href={url} key={`${label}${url}`}>
										<HeaderNavBarDropMenu tree={children} url={url ?? ''} label={label} />
									</LinkWrap>
								))}
								<HeaderCategoryTemporal />
							</Stack>
						</Fade>
					) : null}

					<Stack
						direction="row"
						sx={
							isSearchFocused
								? { width: '88%', position: 'relative', marginLeft: '0px !important' }
								: {}
						}
					>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="flex-end"
							spacing={0.5}
							sx={
								isSearchFocused
									? {
											width: '100%',
											position: 'relative',
											marginLeft: '0px !important',
											marginRight: '10px',
									  }
									: { marginRight: '15px' }
							}
						>
							<HeaderSearch
								ref={headerSearchRef}
								onFocus={handleSearchFocus}
								onBlur={handleSearchBlur}
								onSuggestChange={handleSuggestChange}
								onSuggestProductsChange={handleSuggestProductsChange}
								onSearchValue={handleSearchValue}
								isSearchOpen={isSearchFocused}
							/>
							{isCloseIconVisible ? (
								<IconButton
									sx={{
										color: '#2C2C2C',
										padding: '0px',
										marginLeft: '4.56px !important',
										'&:hover': {
											backgroundColor: 'transparent',
										},
									}}
									// aria-label={SearchNLS.SearchField.t()}
									onClick={() => handleSearchBlur()}
									edge="start"
								>
									<CloseIcon sx={{ color: '#2C2C2C', fontSize: 'var(--font-size-xl)' }} />
								</IconButton>
							) : null}
						</Stack>
						<Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
							<HeaderAccount mobileBreakpoint="md" />
							<HeaderFavorite mobileBreakpoint="md" />
							<HeaderMiniCart mobileBreakpoint="md" />
						</Stack>
					</Stack>
				</Stack>
			</Grid>

			<Fade in={isContainerVisible} timeout={400}>
				<Box
					onClick={() => handleSearchBlur()}
					sx={{
						position: 'absolute',
						width: '100%',
						top: '100%',
						left: 0,
						transition: 'transform 0.4s, opacity 0.4s',
						opacity: 1,
						transform: 'translateY(0)',
						height: isContainerVisible ? '100vh' : '0px',
						zIndex: 9998,
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
					}}
				>
					<Box
						onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
						sx={{
							top: '100%',
							left: 0,
							width: '100vw',
							minHeight: '431px',
							backgroundColor: 'white',
							boxShadow: 'none',
							zIndex: 9999,
							py: '40px',
						}}
						display={'flex'}
						flexDirection={'row'}
						justifyContent={'center'}
					>
						<Stack
							onClick={(event) => event.stopPropagation()}
							display={'flex'}
							direction={'row'}
							sx={{ width: '100%', maxWidth: '1280px' }}
						>
							{searchValueExternal.length ? (
								<Stack display={'flex'} direction={'row'} spacing={'65px'}>
									<Stack display={'flex'} direction={'column'} spacing={2.5} width={'860px'}>
										<Stack
											display={'flex'}
											direction={'row'}
											spacing={1}
											justifyContent={'space-between'}
										>
											<Stack
												display={'flex'}
												direction={'row'}
												spacing={1}
												alignItems={'center'}
												width={'90%'}
											>
												<SearchIcon sx={{ fontSize: '28px', fontWeight: 'bold' }} />
												<Typography
													sx={{
														fontSize: 'var(--font-size-md)',
														fontWeight: 'bold',
														overflow: 'scroll',
														'&::-webkit-scrollbar': {
															width: '0px',
															height: '5px',
														},
														'&::-webkit-scrollbar-track': {
															backgroundColor: '#f1f1f1',
														},
														'&::-webkit-scrollbar-thumb': {
															backgroundColor: '#888',
															borderRadius: '10px',
															border: '2px solid #f1f1f1',
														},
														'&::-webkit-scrollbar-thumb:hover': {
															backgroundColor: '#555',
														},
													}}
												>
													{searchValueExternal}
												</Typography>
											</Stack>
											{suggestProducts.length ? (
												<Stack display={'flex'} direction={'row'} spacing={1}>
													<Linkable
														onClick={() => handleSearchSubmit({ label: searchValueExternal })}
														href=""
														type="inline"
														sx={{
															textDecoration: 'none',
															'&:hover': { textDecoration: 'underline', color: '#2C2C2C' },
														}}
													>
														<Typography
															sx={{
																fontSize: 'var(--font-size-body)',
																fontWeight: 'normal',
																color: '#2C2C2C',
															}}
														>
															{'Ver todo'}
														</Typography>
													</Linkable>
												</Stack>
											) : null}
										</Stack>
										<Stack display={'flex'} direction={'row'} spacing={2.5}>
											{suggestProducts.length ? (
												suggestProducts.map((element, index) => (
													<Stack
														key={index}
														sx={{ backgroundColor: 'transparent', maxWidth: '200px' }}
														direction={'column'}
														spacing={1}
													>
														<Linkable onClick={() => handleSearchBlur()} href={element.href}>
															<RenderProductImg
																fullImageRaw={element.thumbnail}
																render={(src) => (
																	<Img
																		src={src}
																		sx={{ width: '200px', height: '200px !important' }}
																		alt={element.name}
																	/>
																)}
															/>
															<Typography
																sx={{
																	fontSize: 'var(--font-size-body)',
																	fontWeight: '600',
																	color: '#2C2C2C',
																	letterSpacing: '0px',
																}}
															>
																{element.name}
															</Typography>
															<Typography
																sx={{
																	fontSize: 'var(--font-size-body)',
																	fontWeight: '600',
																	color: '#929292',
																	letterSpacing: '0px',
																}}
															>
																<PriceProductSearch
																	product={element}
																	infoElectronico={element?.infoElectronico}
																/>
															</Typography>
														</Linkable>
													</Stack>
												))
											) : headerSearchRef?.current?.isLoading ? (
												<Typography sx={{ fontSize: 'var(--font-size-md)', fontWeight: 'bold' }}>
													{'Cargando resultados...'}
												</Typography>
											) : (
												<Typography sx={{ fontSize: 'var(--font-size-md)', fontWeight: 'bold' }}>
													{'Sin coincidencias'}
												</Typography>
											)}
										</Stack>
									</Stack>
									<Stack display={'flex'} direction={'column'} spacing={1}>
										{options && options.length ? (
											<Box display={'flex'} gap={'4px'} alignItems={'center'}>
												<SearchIcon sx={{ fontSize: '24px', fontWeight: 'bold' }} />
												<Typography
													sx={{
														fontSize: 'var(--font-size-default)',
														fontWeight: 'bold',
														color: '#2C2C2C',
														letterSpacing: '0px',
													}}
												>
													{'Sugerencias'}
												</Typography>
											</Box>
										) : null}

										{options && options.length
											? options.map((element: { label: string }, index: number) => (
													<HeaderNavBarSuggest
														key={index}
														label={element.label}
														searchMatch={searchValueExternal}
														onClick={() => handleSearchSubmit({ label: element.label })}
													/>
											  ))
											: null}

										{suggest && suggest[1] && suggest[1].entry ? (
											<Box
												display={'flex'}
												gap={'4px'}
												alignItems={'center'}
												marginTop={'20px'}
												marginBottom={'10px'}
											>
												<CheckroomIcon sx={{ fontSize: '24px', fontWeight: 'bold' }} />
												<Typography
													sx={{
														fontSize: 'var(--font-size-default)',
														fontWeight: 'bold',
														color: '#2C2C2C',
														letterSpacing: '0px',
													}}
												>
													{'Categorias'}
												</Typography>
											</Box>
										) : null}

										{suggest && suggest[1] && suggest[1].entry
											? suggest[1].entry
													.slice(0, 3)
													.map(
														({ label, href }: { label: string; href: string }, index: number) => (
															<HeaderNavBarSuggest
																key={index}
																label={label}
																searchMatch={searchValueExternal}
																href={href}
															/>
														)
													)
											: null}
									</Stack>
								</Stack>
							) : (
								<React.Fragment>
									{isSearchFocused ? (
										<Box
											sx={{
												marginLeft: { xs: '80px', xl: '0px' },
											}}
										>
											<HeaderNavBarContentRecommendation
												onClickEntryRecommendation={handleOnClickEntryRecommendation}
											/>
										</Box>
									) : null}
								</React.Fragment>
							)}
						</Stack>
					</Box>
				</Box>
			</Fade>
		</Paper>
	);
};
